import Tabs, { type TabsProps } from "@cloudscape-design/components/tabs"
import { useSearchParams } from "react-router-dom"

interface SharedTabsProps {
  /**
   * Tabs to display. Each needs an `id`, a `label`, and the `content` to render when active.
   */
  tabs: ReadonlyArray<TabsProps.Tab>
  /**
   * Accessible label for the tab list.
   */
  ariaLabel: string
  /**
   * URL search-param key used to persist the active tab. Defaults to `"tab"`.
   */
  paramName?: string
  /**
   * Cloudscape Tabs variant. Defaults to `"default"`.
   */
  variant?: TabsProps["variant"]
  /**
   * Called with the newly-active tab id and the pending search params, before they are committed.
   * Mutate `params` to make additional URL changes (e.g. clearing a sub-tab param) in the same
   * atomic update.
   */
  onTabChange?: (activeTabId: string, params: URLSearchParams) => void
  /**
   * Content rendered on the tab-list row, right-aligned next to the tabs (e.g. action buttons).
   * Maps to Cloudscape Tabs' `actions` slot.
   */
  actions?: React.ReactNode
  /**
   * When true, selecting a tab smooth-scrolls the window back to the top, so the user lands at the
   * start of the newly-active tab instead of mid-content. Defaults to `false`.
   */
  scrollToTopOnChange?: boolean
}

/**
 * Horizontal tab bar backed by Cloudscape Tabs, with the active tab persisted to a URL search param
 * (`?tab=`). The first tab is active when the param is absent or doesn't match a known tab.
 * Selecting a tab replaces the param without adding a history entry.
 */
const SharedTabs = ({
  tabs,
  ariaLabel,
  paramName = "tab",
  variant,
  onTabChange,
  actions,
  scrollToTopOnChange = false,
}: SharedTabsProps) => {
  const [searchParams, setSearchParams] = useSearchParams()

  const paramValue = searchParams.get(paramName)
  const activeTabId = tabs.some((tab) => tab.id === paramValue) ? paramValue! : tabs[0]?.id

  const handleChange = (activeTab: string) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        next.set(paramName, activeTab)
        onTabChange?.(activeTab, next)
        return next
      },
      { replace: true },
    )
    if (scrollToTopOnChange) {
      window.scrollTo({ top: 0, behavior: "smooth" })
    }
  }

  return (
    <Tabs
      tabs={tabs}
      activeTabId={activeTabId}
      onChange={({ detail }) => handleChange(detail.activeTabId)}
      ariaLabel={ariaLabel}
      variant={variant}
      actions={actions}
    />
  )
}

export default SharedTabs
